/**
 * Utilities for cleaning and formatting Google Maps embed URLs
 */

export const DEFAULT_MAP_FALLBACK = "https://www.google.com/maps?q=Permata%20Harapan%20School%20Jakarta&output=embed";

/**
 * Extracts and sanitizes a valid Google Maps embed URL from user input,
 * which may be a raw URL, an <iframe> HTML snippet, or a standard Google Maps link.
 * 
 * @param {string} raw - The raw input from user/database
 * @param {string} [fallbackAddress] - Optional address to generate search embed if URL invalid
 * @returns {string} Clean Google Maps embed URL suitable for iframe src
 */
export function cleanGoogleMapsEmbedUrl(raw, fallbackAddress = '') {
  const fallback = fallbackAddress && typeof fallbackAddress === 'string' && fallbackAddress.trim().length > 3
    ? `https://www.google.com/maps?q=${encodeURIComponent(fallbackAddress.trim())}&output=embed`
    : DEFAULT_MAP_FALLBACK;

  if (!raw || typeof raw !== 'string') {
    return fallback;
  }

  let str = raw.trim();

  // 1. If user pasted the whole HTML <iframe> tag, extract the src attribute
  // Example: <iframe src="https://www.google.com/maps/embed?pb=..." ...></iframe>
  // Also handles escaped quotes: src=\"...\"
  const iframeSrcMatch = str.match(/src=\\?["']([^"'\\]+)\\?["']/i);
  if (iframeSrcMatch && iframeSrcMatch[1]) {
    str = iframeSrcMatch[1].trim();
  } else if (str.toLowerCase().includes('<iframe')) {
    // Tag was pasted without clean closing quotes
    const fallbackMatch = str.match(/https?:\/\/[^\s"'<>]+/i);
    if (fallbackMatch && fallbackMatch[0]) {
      str = fallbackMatch[0].trim();
    }
  }

  // Strip any lingering HTML tags, quotes, or trailing slashes
  str = str.replace(/<[^>]+>/g, '').replace(/^["']|["']$/g, '').trim();

  // 2. Validate that it starts with http:// or https://
  // Without this check, a string starting with "<iframe..." will be treated as a relative URL by the browser,
  // causing the website to load itself inside the iframe ("website in website" bug).
  if (!/^https?:\/\//i.test(str)) {
    return fallback;
  }

  // 3. If it is already a Google Maps embed URL
  if (str.includes('/maps/embed') || str.includes('output=embed')) {
    return str;
  }

  // 4. If it is a standard Google Maps URL, convert to embed format:
  if (str.includes('google.com/maps') || str.includes('maps.google.com')) {
    // Handle /place/ URLs
    if (str.includes('/place/')) {
      const placeMatch = str.match(/\/place\/([^/@?]+)/);
      if (placeMatch && placeMatch[1]) {
        try {
          const placeName = decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
          return `https://www.google.com/maps?q=${encodeURIComponent(placeName)}&output=embed`;
        } catch {
          return `https://www.google.com/maps?q=${placeMatch[1]}&output=embed`;
        }
      }
    }

    // Handle search query URLs (maps?q=...)
    if (str.includes('?q=') || str.includes('&q=')) {
      return str + (str.includes('?') ? '&output=embed' : '?output=embed');
    }
  }

  return str;
}
